import { Dispatch } from "react";
import { constants } from "../../../../../constants";
import IconSVG from "../../../../../styles/svg-icons";
import { OriginatingTransactionClassStatus } from "../../../../../types/amr-pipeline/enums/OriginatingTransactionClassStatus";
import { TransactionStatus } from "../../../../../types/amr-pipeline/enums/TransactionStatus";
import { TransactionType } from "../../../../../types/amr-pipeline/enums/TransactionType";
import { AmrClass } from "../../../../../types/amr-pipeline/models/AmrClass";
import { AmrTransaction } from "../../../../../types/amr-pipeline/models/AmrTransaction";
import { Company } from "../../../../../types/amr-pipeline/models/Company";
import { OriginatingTransaction } from "../../../../../types/amr-pipeline/models/OriginatingTransaction";
import { OriginatingTransactionClass } from "../../../../../types/amr-pipeline/models/OriginatingTransactionClass";
import { isBrokerDealersOwnTransaction } from "../../../../../utils/amr-pipeline.utils";
import { IColumnDefinition } from "../../../../bidding/common/table/types/ColumnDefinition";
import { TransactionHistoryLink } from "../../../common/TransactionHistoryLink";
import { PipelineColumn } from "../../../types/PipelineColumn";
import { IssuanceMonitorTab } from "../../../types/PipelineTabTypes";
import { IntexButton } from "../../IntexButton/IntexButton";
import { OptionsButton } from "../../OptionsButton";
import { ImSubscriptionActionBlocker } from "../../../subscription/ImSubscriptionActionBlocker";
import { BloombergLink } from "../../../../common/BloombergLink";
import { ValitanaLink } from "../../../../common/ValitanaLink";
import { TransactionAlertsButton } from "../../../common/transactionAlerts/TransactionAlertsButton";
import { BwicMonitorLink } from "../../../common/BwicMonitorLink";
import { BwicInventoryLink } from "../../../common/BwicInventoryLink";
import { AddToPortfolioLink } from "../../../common/AddToPortfolioLink";
import { ShareTransactionButton } from "../../../detailed/new-issue/share-transaction/ShareTransactionButton";
import { FilterRelatedTransactionsIcon } from "../../../common/FilterRelatedTransactionsIcon";

export const shareOptionColumnDefinition: IColumnDefinition<
    OriginatingTransactionClass | AmrClass
> = {
    columnKey: PipelineColumn.shareOption,
    renderColumnHeaderContent: () => "",
    renderColumnContent: (
        transactionClass,
        context: {
            userCompany: Company;
            setTransactionToShare: (
                transaction: OriginatingTransaction | AmrTransaction
            ) => void;
        }
    ) => {
        const { transaction } = transactionClass;
        const { userCompany, setTransactionToShare } = context;
        const showShare = isBrokerDealersOwnTransaction(
            transaction,
            userCompany
        );

        return (
            <div className="action-row">
                <div className="action-col">
                    {showShare ? (
                        <div onClick={(e) => e.stopPropagation()}>
                            <ShareTransactionButton
                                className="btn-link"
                                transaction={transaction}
                                handleShare={() =>
                                    setTransactionToShare(transaction)
                                }
                            >
                                <IconSVG name="share" width={16} height={16} />
                            </ShareTransactionButton>
                        </div>
                    ) : (
                        <span className="action-empty-placeholder">
                            {constants.emptyPlaceholder}
                        </span>
                    )}
                </div>
            </div>
        );
    },
    headerClassName:
        "data-list-cell-xxxs data-list-cell-actions padding-r-0 text-right",
    bodyClassName:
        "data-list-cell-xxxs data-list-cell-actions padding-r-0 text-right",
    stickRight: true,
};

export const optionsColumnDefinition: IColumnDefinition<
    OriginatingTransactionClass | AmrClass
> = {
    columnKey: PipelineColumn.optionsAction,
    renderColumnHeaderContent: () => "",
    renderColumnContent: (
        transactionClass,
        context: {
            userCompany: Company;
            dispatch: Dispatch<any>;
            setTab: (tab: IssuanceMonitorTab) => void;
            setTransactionForIntex: (
                transaction: OriginatingTransaction
            ) => void;
            setTransactionForAlerts: (
                transaction: OriginatingTransaction
            ) => void;
        }
    ) => {
        const {
            userCompany,
            setTab,
            setTransactionForIntex,
            setTransactionForAlerts,
        } = context;
        const { transaction } = transactionClass as OriginatingTransactionClass;
        const {
            referenceName,
            dealReferenceName,
            dealLegalName,
            numberOfDealPublishedTransactions,
            status,
            type,
            version,
        } = transaction;
        const isOriginatingTransaction = type !== TransactionType.amr;
        const showHistory =
            status !== TransactionStatus.Draft && isOriginatingTransaction;

        const transactionWithClass = {
            ...transaction,
            classes: [transactionClass],
        } as OriginatingTransaction | AmrTransaction;

        const classIdentifiers = isOriginatingTransaction
            ? (transactionClass as OriginatingTransactionClass).tranche
            : (transactionClass as AmrClass).class;

        return (
            <div className="action-row">
                <div
                    className="action-col"
                    onClick={(e) => e.stopPropagation()}
                >
                    {numberOfDealPublishedTransactions > 1 ? (
                        <ImSubscriptionActionBlocker>
                            {(blocked) => (
                                <FilterRelatedTransactionsIcon 
                                    dealLegalName={dealLegalName}
                                    dealReferenceName={dealReferenceName}
                                    disabled={blocked}
                                    onClick={() => setTab(IssuanceMonitorTab.Deals)}
                                />
                            )}
                        </ImSubscriptionActionBlocker>
                    ) : (
                        <span className="action-empty-placeholder">
                            {constants.emptyPlaceholder}
                        </span>
                    )}
                </div>
                <div
                    className="action-col"
                    onClick={(e) => e.stopPropagation()}
                >
                    {isOriginatingTransaction &&
                    status !== TransactionStatus.Closed ? (
                        <div onClick={(e) => e.stopPropagation()}>
                            <TransactionAlertsButton
                                transaction={transaction}
                                onClick={() =>
                                    setTransactionForAlerts(transaction)
                                }
                            />
                        </div>
                    ) : (
                        <span className="action-empty-placeholder">
                            {constants.emptyPlaceholder}
                        </span>
                    )}
                </div>
                <div
                    className="action-col"
                    onClick={(e) => e.stopPropagation()}
                >
                    {showHistory ? (
                        <ImSubscriptionActionBlocker>
                            {(blocked) => (
                                <div onClick={(e) => e.stopPropagation()}>
                                    <TransactionHistoryLink
                                        referenceName={referenceName}
                                        disabled={blocked}
                                        dealReferenceName={dealReferenceName}
                                        label={false}
                                        version={version}
                                    />
                                </div>
                            )}
                        </ImSubscriptionActionBlocker>
                    ) : (
                        <span className="action-empty-placeholder">
                            {constants.emptyPlaceholder}
                        </span>
                    )}
                </div>
                <div
                    className="action-col"
                    onClick={(e) => e.stopPropagation()}
                >
                    <IntexButton
                        transaction={transactionWithClass}
                        userCompany={userCompany}
                        onClick={setTransactionForIntex}
                        placeholderIfEmpty
                        secondaryPlaceholderColor
                    />
                </div>
                <div
                    className="action-col action-col-bwic-monitor-link"
                    onClick={(e) => e.stopPropagation()}
                >
                    <BwicMonitorLink
                        currency={transaction.currency}
                        identifiers={classIdentifiers}
                        secondaryPlaceholderColor
                    />
                </div>
                <div
                    className="action-col action-col-bwic-inventory-link"
                    onClick={(e) => e.stopPropagation()}
                >
                    <BwicInventoryLink
                        currency={transaction.currency}
                        identifiers={classIdentifiers}
                        secondaryPlaceholderColor
                    />
                </div>
                <div
                    className="action-col action-col-bloomberg"
                    onClick={(e) => e.stopPropagation()}
                >
                    <BloombergLink
                        identifiers={classIdentifiers}
                        placeholderIfEmpty
                        secondaryPlaceholderColor
                    />
                </div>
                <div
                    className="action-col action-col-bloomberg"
                    onClick={(e) => e.stopPropagation()}
                >
                    <AddToPortfolioLink
                        transaction={transaction}
                        transactionClass={transactionClass}
                        checkSubscription
                    />
                </div>

                <div
                    className="action-col action-col-valitana"
                    onClick={(e) => e.stopPropagation()}
                >
                    <ValitanaLink
                        identifiers={classIdentifiers}
                        placeholderIfEmpty
                        secondaryPlaceholderColor
                    />
                </div>
            </div>
        );
    },
    headerClassName:
        "data-list-cell-actions-wrap padding-r-0 data-list-cell-actions text-right",
    bodyClassName:
        "data-list-cell-actions-wrap padding-r-0 data-list-cell-actions text-right",
    stickRight: true,
};

export const optionsButtonColumnDefinition: IColumnDefinition<
    OriginatingTransactionClass | AmrClass
> = {
    columnKey: PipelineColumn.optionButton,
    renderColumnHeaderContent: () => "",
    renderColumnContent: (
        transactionClass,
        context: {
            userCompany: Company;
        }
    ) => {
        const { userCompany } = context;
        const { transaction, originatingTransactionClassStatus, numberOfIois } =
            transactionClass as OriginatingTransactionClass;

        const isAllowedClassStatus = ![
            OriginatingTransactionClassStatus.NotOffered,
            OriginatingTransactionClassStatus.Subject,
        ].includes(originatingTransactionClassStatus);

        if (!isAllowedClassStatus) {
            return null;
        }

        return (
            <span onClick={(e) => e.stopPropagation()}>
                <OptionsButton
                    transaction={transaction}
                    userCompany={userCompany}
                    withIOIs={!!numberOfIois}
                />
            </span>
        );
    },
    headerClassName: "data-list-cell-sm-01 keep-while-collapsed padding-l-0 data-list-cell-actions-btn text-right",
    bodyClassName: "data-list-cell-sm-01 keep-while-collapsed padding-l-0 data-list-cell-actions-btn text-right",
    stickRight: true,
};
